import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@/api/brandApi';
import type { Category } from '@/api/categoryApi';
import type { StockStatus } from '../productManagement';

interface ProductToolbarProps {
  searchValue: string;
  selectedCategoryId: string;
  selectedBrandId: string;
  selectedStockStatus: StockStatus | '';
  categories: Category[];
  brands: Brand[];
  isCategoryLoading: boolean;
  isBrandLoading: boolean;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onBrandChange: (value: string) => void;
  onStockStatusChange: (value: StockStatus | '') => void;
}

const controlClassName = 'h-11 min-w-0 rounded-xl border border-outline-variant bg-white px-3 text-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container disabled:text-secondary';

const ProductToolbar: React.FC<ProductToolbarProps> = ({
  searchValue,
  selectedCategoryId,
  selectedBrandId,
  selectedStockStatus,
  categories,
  brands,
  isCategoryLoading,
  isBrandLoading,
  onSearchChange,
  onCategoryChange,
  onBrandChange,
  onStockStatusChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="border-b border-outline-variant bg-white px-3 py-3 sm:px-5 sm:py-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(280px,1fr)_minmax(170px,220px)_minmax(170px,220px)_minmax(160px,200px)]">
        <label className="relative min-w-0 sm:col-span-2 xl:col-span-1">
          <span className="sr-only">{t('admin.products.filters.search_label')}</span>
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[20px] text-outline">search</span>
          <input
            type="search"
            className="h-11 w-full rounded-xl border border-outline-variant bg-white pl-10 pr-3 text-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            placeholder={t('admin.products.filters.search_placeholder')}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </label>

        <label className="min-w-0">
          <span className="sr-only">{t('admin.products.filters.category_label')}</span>
          <select
            className={`${controlClassName} w-full`}
            value={selectedCategoryId}
            onChange={(event) => onCategoryChange(event.target.value)}
            disabled={isCategoryLoading}
          >
            <option value="">{isCategoryLoading ? t('admin.products.filters.loading_categories') : t('admin.products.filters.all_categories')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </label>

        <label className="min-w-0">
          <span className="sr-only">{t('admin.products.filters.brand_label')}</span>
          <select
            className={`${controlClassName} w-full`}
            value={selectedBrandId}
            onChange={(event) => onBrandChange(event.target.value)}
            disabled={isBrandLoading}
          >
            <option value="">{isBrandLoading ? t('admin.products.filters.loading_brands') : t('admin.products.filters.all_brands')}</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>{brand.name}</option>
            ))}
          </select>
        </label>

        <label className="min-w-0">
          <span className="sr-only">{t('admin.products.filters.stock_label')}</span>
          <select
            className={`${controlClassName} w-full`}
            value={selectedStockStatus}
            onChange={(event) => onStockStatusChange(event.target.value as StockStatus | '')}
          >
            <option value="">{t('admin.products.filters.all_stock')}</option>
            <option value="in">{t('admin.products.stock.in')}</option>
            <option value="low">{t('admin.products.stock.low')}</option>
            <option value="out">{t('admin.products.stock.out')}</option>
          </select>
        </label>
      </div>
    </div>
  );
};

export default ProductToolbar;
